import { FC, useEffect, useState } from 'react';
import {
  Contact,
  ContactType,
  IdentityCheckFlowDevelopment,
  IdentityCheckFlowLegalPerson,
  VerifyOtpForIdentityCheckFlowRes,
  VerifyOtpRes,
} from '~/api/types';
import config from '~/config';
import { DevelopmentSelectionScreen } from '../screens/development-selection';
import { IdentityCheckSuccessScreen } from '../screens/identity-check-success';
import { LegalPersonSelectionScreen } from '../screens/legal-person-selection';
import { OtpSendingScreen } from '../screens/otp-sending';
import { OtpVerificationScreen } from '../screens/otp-verification';
import { Header } from '../header';
import { Footer } from '../footer';
import { getIdentificationSessionPreferences, queryLegalPersonForIdentityCheckFlow } from '~/api';
import { sendOtpForIdentityCheckFlow, resendOtpForIdentityCheckFlow, verifyOtpForIdentityCheckFlow } from '~/api';
import { ErrorScreen } from '../screens/error';
import { LoadingScreen } from '../screens/loading';

export const IdentityValidationWizard: FC = () => {
  // the url pathname is /validar/{id}, extract the id using regex
  const identificationSessionId = window.location.pathname.match(/\/validar\/(.+)/)?.[1] || null;

  const [step, setStep] = useState<Step>('legal-person-selection');
  const [selectedLegalPerson, setSelectedLegalPerson] = useState<IdentityCheckFlowLegalPerson | null>(null);
  const [selectedDevelopment, setSelectedDevelopment] = useState<IdentityCheckFlowDevelopment | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState<string | null>(null);
  /**
   * Whether the identification session behind the url is still usable. The
   * backend answers as if it did not exist both when it was never created and
   * when its 24h already elapsed, so there is a single "gone" state: either way
   * the customer has to start over from the WhatsApp conversation.
   */
  const [identificationSessionStatus, setIdentificationSessionStatus] =
    useState<IdentificationSessionStatus>('loading');

  const CURRENT_CUB_MAGIC_LINK = window.location.host;
  const IS_WHITE_LABEL = config.CUB_HOST !== CURRENT_CUB_MAGIC_LINK;

  useEffect(() => {
    if (!identificationSessionId) return;

    getIdentificationSessionPreferences(identificationSessionId)
      .then((preferences) => {
        if (!preferences) return setIdentificationSessionStatus('expired-or-not-found');

        // the preferences themselves are not read: the customer always goes
        // back to the attendant after validating, so the request only tells
        // whether the identification session still exists
        setIdentificationSessionStatus('valid');
      })
      .catch((error: unknown) => {
        console.error(error);
        setIdentificationSessionStatus('error');
      });
  }, [identificationSessionId]);

  const queryLegalPerson = async (doc: string) => {
    if (!identificationSessionId) {
      console.warn('No identificationSessionId found');
      return Promise.reject(new Error('No identificationSessionId found'));
    }

    return await queryLegalPersonForIdentityCheckFlow(doc, identificationSessionId);
  };

  const onLegalPersonSelection = (legalPerson: IdentityCheckFlowLegalPerson, sessionId?: string) => {
    if (!sessionId) {
      console.warn('No sessionId returned');
      return;
    }

    setSelectedLegalPerson(legalPerson);
    setSessionId(sessionId);

    const filteredDevelopments = legalPerson.developments.filter((d) => {
      // always show all contracts for non-white-label deployments
      if (!IS_WHITE_LABEL) return true;

      // show only contracts allowed of current magic link white-label
      const sanitizedMagicLinkUrl = d.magicLinkUrl?.replace('https://', '').replace('/', '');
      if (sanitizedMagicLinkUrl === CURRENT_CUB_MAGIC_LINK) return true;

      return false;
    });

    const onlyDevelopment = filteredDevelopments.length === 1 ? filteredDevelopments[0] : undefined;

    if (onlyDevelopment && !onlyDevelopment.customerPortalUrl) {
      // we can skip the development selection directly to the contact selection for otp delivery step
      onDevelopmentSelection(onlyDevelopment);
    } else {
      setStep('development-selection');
    }
  };

  const onDevelopmentSelection = (customer: IdentityCheckFlowDevelopment) => {
    setSelectedDevelopment(customer);
    setStep('otp-sending');
  };

  const onContactSelection = (contact: Contact, otpId: string) => {
    setSelectedContact(contact);
    setOtpId(otpId);
    setStep('otp-verification');
  };

  /**
   * Currently, we support just one legal person per session. So, in order to
   * change the legal person, we need to refresh the page and start over.
   */
  const goBackFromDevelopmentSelection = () => {
    window.location.reload();
  };

  const goBackFromOtpSending = () => {
    setSelectedDevelopment(null);
    setStep('development-selection');
  };

  const onOtpConfirmation = (res: VerifyOtpForIdentityCheckFlowRes | VerifyOtpRes) => {
    if ('businessPhoneNumber' in res) {
      setBusinessPhoneNumber(res.businessPhoneNumber);
      setStep('identity-check-success');
    }
  };

  /**
   * The link is only valid for 24h and each identity check has its own: an
   * expired one is indistinguishable from one that never existed, so both lead
   * the customer back to the WhatsApp conversation to start another.
   */
  if (!identificationSessionId || identificationSessionStatus === 'expired-or-not-found') {
    return (
      <>
        <Header showExitIdentityCheckFlowButton />
        <ErrorScreen
          title={'Link expirado ou não encontrado'}
          message={
            'Este link de validação de identidade expirou ou não existe. Inicie o processo novamente no canal de WhatsApp.'
          }
        />
        <Footer />
      </>
    );
  }

  if (identificationSessionStatus === 'error') {
    return (
      <>
        <Header showExitIdentityCheckFlowButton />
        <ErrorScreen
          title={'Ocorreu um erro'}
          message={'Inicie o processo de identificação novamente no canal de WhatsApp'}
        />
        <Footer />
      </>
    );
  }

  if (identificationSessionStatus === 'loading') {
    return (
      <>
        <Header showExitIdentityCheckFlowButton />
        <LoadingScreen message="Carregando..." />
        <Footer />
      </>
    );
  }

  switch (step) {
    case 'legal-person-selection': {
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <LegalPersonSelectionScreen
            title="Validação de identidade"
            description="Para continuar com segurança, precisamos confirmar sua identidade. Digite o documento do titular do contrato."
            buttonText="Iniciar"
            queryLegalPerson={queryLegalPerson}
            onLegalPersonSelection={onLegalPersonSelection}
          />
          <Footer />
        </>
      );
    }
    case 'development-selection': {
      if (!selectedLegalPerson) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <DevelopmentSelectionScreen
            title="Escolha o empreendimento"
            person={selectedLegalPerson}
            onDevelopmentSelection={onDevelopmentSelection}
            goBack={goBackFromDevelopmentSelection}
          />
          <Footer />
        </>
      );
    }
    case 'otp-sending': {
      if (!selectedLegalPerson || !selectedDevelopment) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <OtpSendingScreen
            title="Onde quer receber o código?"
            description="Vamos enviar um código de verificação de 6 dígitos. Você volta para esta tela e digita ele em seguida."
            person={selectedLegalPerson}
            development={selectedDevelopment}
            onOtpSent={onContactSelection}
            goBack={goBackFromOtpSending}
            sendOtp={(document: string, developmentId: number, contactType: ContactType, contactId: number) => {
              if (!sessionId) {
                // this should not happen
                console.warn('No sessionId found');
                return Promise.reject(new Error('No sessionId found'));
              }

              return sendOtpForIdentityCheckFlow(document, developmentId, contactType, contactId, sessionId);
            }}
          />
          <Footer />
        </>
      );
    }
    case 'otp-verification': {
      if (!selectedContact || !otpId) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <OtpVerificationScreen
            title="Digite o código que enviamos"
            description="Por favor, digite o código de verificação"
            buttonText="Confirmar código"
            contact={selectedContact}
            otpId={otpId}
            resendOtp={(otpId) => {
              if (!sessionId) {
                // this should not happen
                console.warn('No sessionId found');
                return Promise.reject(new Error('No sessionId found'));
              }

              return resendOtpForIdentityCheckFlow(otpId, sessionId);
            }}
            verifyOtp={(otpId, token) => {
              if (!sessionId) {
                // this should not happen
                console.warn('No sessionId found');
                return Promise.reject(new Error('No sessionId found'));
              }

              return verifyOtpForIdentityCheckFlow(otpId, token, sessionId, identificationSessionId);
            }}
            onOtpConfirmation={onOtpConfirmation}
          />
          <Footer />
        </>
      );
    }
    case 'identity-check-success': {
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <IdentityCheckSuccessScreen whatsappNumber={businessPhoneNumber} />
          <Footer />
        </>
      );
    }
  }
};

type IdentificationSessionStatus = 'loading' | 'valid' | 'expired-or-not-found' | 'error';

type Step =
  | 'legal-person-selection'
  | 'development-selection'
  | 'otp-sending'
  | 'otp-verification'
  | 'identity-check-success';
